import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaMoneyBillWave, FaWallet, FaFilePdf, FaTimes, FaTruckLoading, FaArrowDown, FaArrowUp, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import { generateStatementPDF } from '../utils/pdfGenerator';
import saleService from '../services/saleService';

const CurrentAccountDetailPage = () => {
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'borc', 'alacak'
  
  // Teslimat Modal State
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState(null);

  const handleDeliveryClick = async (item) => {
    if (item.type !== 'teslimat' || !item.sale_id) return;
    
    try {
      const sale = await saleService.getById(item.sale_id);
      setDeliveryDetails({
        ...item,
        saleItems: sale.items || []
      });
      setShowDeliveryModal(true);
    } catch (error) {
      toast.error('Teslimat detayları alınamadı.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accRes, histRes] = await Promise.all([
          api.get(`/current-accounts/${id}`),
          api.get(`/current-accounts/${id}/history`)
        ]);
        
        setAccount(accRes.data);
        // Verinin dizi olduğundan emin oluyoruz
        const historyData = Array.isArray(histRes.data) ? histRes.data : [];
        setHistory(historyData);
      } catch (error) {
        console.error(error);
        toast.error('Veriler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Güvenli Tarih Formatlama Fonksiyonu
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
    } catch (error) {
      return '-';
    }
  };

  // Bakiye Hesaplama Mantığı (useMemo ile optimize edildi)
  const { carryOverBalance, filteredHistory, finalBalance, totalDebt, totalCredit } = useMemo(() => {
    if (!account || !Array.isArray(history)) return { carryOverBalance: 0, filteredHistory: [], finalBalance: 0, totalDebt: 0, totalCredit: 0 };

    let carryOver = 0;
    let period = 0;
    let tDebt = 0;
    let tCredit = 0;
    const filtered = [];

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (end) end.setHours(23, 59, 59, 999); // Günün sonuna kadar

    history.forEach(item => {
      const itemDate = new Date(item.date);
      const amount = parseFloat(item.amount);
      let signedAmount = 0;
      let isDebtMovement = false; // Borç hareketi mi?
      let isDebit = false; // Muhasebe anlamında Borç mu?

      // İşlem yönüne göre tutarı belirle (+/-)
      if (account.account_type === 'musteri') {
        // Müşteri Hesabı:
        // Borç (Satış) -> Bakiye Artar (+)
        // Ödeme (Bizden Müşteriye Para Çıkışı/İade) -> Bakiye Artar (+)
        // Alacak (İade) -> Bakiye Azalır (-)
        // Tahsilat (Müşteriden Para Girişi) -> Bakiye Azalır (-)
        if (item.type === 'borc' || item.type === 'odeme') {
          signedAmount = amount;
          isDebtMovement = true;
          isDebit = true;
        } else if (item.type === 'alacak' || item.type === 'tahsilat') {
          signedAmount = -amount;
          isDebtMovement = false;
          isDebit = false;
        }
      } else {
        // Tedarikçi Hesabı:
        // Alacak (Alış) -> Bakiye Artar (+) (Tedarikçiye borçlandık)
        // Borç (İade), Ödeme (Para Çıkışı), Tahsilat (Para Girişi/İade) -> Bakiye Azalır (-)
        if (item.type === 'alacak') {
          signedAmount = amount;
          isDebtMovement = false; // Tedarikçi için Alacak hareketi (Bizim borcumuz artıyor ama hareket tipi Alacak)
          isDebit = false; // Alacak
        } else if (item.type === 'borc' || item.type === 'odeme' || item.type === 'tahsilat') {
          signedAmount = -amount;
          isDebtMovement = true;
          isDebit = true; // Borç (Borcumuz azalıyor)
        }
      }

      // Tarih kontrolü
      if (start && itemDate < start) {
        carryOver += signedAmount;
      } else if ((!start || itemDate >= start) && (!end || itemDate <= end)) {
        period += signedAmount;
        
        // Tip Filtreleme
        let matchesFilter = true;
        
        if (filterType === 'borc_group') {
          if (!isDebtMovement || item.type === 'teslimat') matchesFilter = false;
        } else if (filterType === 'alacak_group') {
          if (isDebtMovement || item.type === 'teslimat') matchesFilter = false;
        } else if (filterType !== 'all') {
          if (item.type !== filterType) matchesFilter = false;
        }

        if (matchesFilter) filtered.push(item);
        
        // Toplamları Hesapla (Filtreye uyanlar)
        if (matchesFilter && item.type !== 'teslimat') {
           if (isDebit) tDebt += amount;
           else tCredit += amount;
        }
      }
    });

    return { 
      carryOverBalance: carryOver, 
      filteredHistory: filtered, 
      finalBalance: carryOver + period,
      totalDebt: tDebt,
      totalCredit: tCredit
    };
  }, [history, startDate, endDate, account, filterType]);

  const handleDownloadExcel = () => {
    if (!filteredHistory || filteredHistory.length === 0) {
      toast.warning('İndirilecek veri bulunamadı.');
      return;
    }

    const headers = ["Tarih", "İşlem Türü", "Açıklama", "Tutar"];
    const rows = filteredHistory.map(item => {
      const date = new Date(item.date).toLocaleDateString('tr-TR');
      
      let typeLabel = item.type;
      if (item.type === 'borc') typeLabel = 'Borç Fişi';
      else if (item.type === 'alacak') typeLabel = 'Alacak Fişi';
      else if (item.type === 'tahsilat') typeLabel = 'Tahsilat';
      else if (item.type === 'odeme') typeLabel = 'Ödeme';
      else if (item.type === 'teslimat') typeLabel = 'Teslimat';
      
      if (item.source_type === 'islem' && item.sale_id) typeLabel = 'Satış Faturası';

      const description = (item.description || '').replace(/;/g, ' ').replace(/[\r\n]+/g, ' ');
      const amount = item.type === 'teslimat' ? '0' : new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, useGrouping: false }).format(item.amount);

      return [date, typeLabel, description, amount];
    });

    // Toplamları Ekle
    const formatNum = (num) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, useGrouping: false }).format(num);
    
    rows.push(["", "", "", ""]); // Boş satır
    rows.push(["", "", "TOPLAM BORÇ", formatNum(totalDebt)]);
    rows.push(["", "", "TOPLAM ALACAK", formatNum(totalCredit)]);
    
    const balanceLabel = finalBalance > 0 ? (account.account_type === 'musteri' ? '(Borçlu)' : '(Alacaklı)') : finalBalance < 0 ? (account.account_type === 'musteri' ? '(Alacaklı)' : '(Borçlu)') : '(Dengede)';
    rows.push(["", "", `GENEL BAKİYE ${balanceLabel}`, formatNum(Math.abs(finalBalance))]);

    const csvContent = [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n");

    const blob = new Blob(["\ufeff", csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Hareket_Gecmisi_${account.company_name}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info('PDF hazırlanıyor...');
      const settingsRes = await api.get('/settings');
      await generateStatementPDF(account, filteredHistory, finalBalance, carryOverBalance, startDate, endDate, settingsRes.data);
    } catch (error) {
      toast.error('PDF oluşturulurken hata oluştu.');
    }
  };

  if (loading) return <div>Yükleniyor...</div>;
  if (!account) return <div>Cari bulunamadı.</div>;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center mb-4">
        <Link to="/current-accounts" className="btn btn-outline-secondary me-3">
          <FaArrowLeft /> Geri
        </Link>
        <h2 className="mb-0 me-auto">{account.company_name} - Hareket Detayı</h2>
        <div>
          <button onClick={handleDownloadExcel} className="btn btn-outline-success me-2">
            <FaFileExcel className="me-2" /> Excel
          </button>
          <button onClick={handleDownloadPDF} className="btn btn-outline-danger">
            <FaFilePdf className="me-2" /> Ekstre İndir
          </button>
        </div>
      </div>

      {/* Üst Bilgi Kartı */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Yetkili:</strong> {account.authorized_person || '-'}</p>
                  <p><strong>Telefon:</strong> {account.phone || '-'}</p>
                  <p><strong>E-posta:</strong> {account.email || '-'}</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Vergi No:</strong> {account.tax_number || '-'}</p>
                  <p><strong>Vergi Dairesi:</strong> {account.tax_office || '-'}</p>
                  <p><strong>Adres:</strong> {account.address || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={`card text-white h-100 ${finalBalance > 0 ? 'bg-danger' : 'bg-success'}`}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center">
              <h5 className="card-title">Güncel Bakiye</h5>
              <h2 className="fw-bold">
                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Math.abs(finalBalance))}
              </h2>
              <p className="mb-0">{finalBalance > 0 ? (account.account_type === 'musteri' ? '(Borçlu)' : '(Alacaklı)') : finalBalance < 0 ? (account.account_type === 'musteri' ? '(Alacaklı)' : '(Borçlu)') : '(Dengede)'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hareket Tablosu */}
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <h5 className="mb-0">Hareket Geçmişi</h5>
        </div>
        {/* Filtreleme Alanı */}
        <div className="card-body border-bottom bg-light">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-bold">Başlangıç Tarihi</label>
              <input type="date" className="form-control form-control-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold">Bitiş Tarihi</label>
              <input type="date" className="form-control form-control-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold">İşlem Tipi</label>
              <select className="form-select form-select-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">Tümü</option>
                <option value="borc_group">Borçlandırıcı İşlemler</option>
                <option value="alacak_group">Alacaklandırıcı İşlemler</option>
                <option disabled>──────────</option>
                <option value="borc">Satış / Borç Fişi</option>
                <option value="alacak">İade / Alacak Fişi</option>
                <option value="tahsilat">Tahsilat</option>
                <option value="odeme">Ödeme</option>
                <option value="teslimat">Teslimat</option>
              </select>
            </div>
            <div className="col-md-2">
              {(startDate || endDate || filterType !== 'all') && (
                <button className="btn btn-sm btn-outline-secondary w-100" onClick={() => { setStartDate(''); setEndDate(''); setFilterType('all'); }}>
                  <FaTimes className="me-1" /> Temizle
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Özet Kartları (Tablo Üstü) */}
        <div className="card-body border-bottom bg-white py-3">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="p-3 border rounded bg-light d-flex justify-content-between align-items-center h-100">
                <div>
                  <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '0.75rem'}}>Toplam Borç</small>
                  <span className="fw-bold fs-5 text-dark">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalDebt)}</span>
                </div>
                <div className="bg-white p-2 rounded-circle shadow-sm"><FaArrowDown className="text-danger" /></div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded bg-light d-flex justify-content-between align-items-center h-100">
                <div>
                  <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '0.75rem'}}>Toplam Alacak</small>
                  <span className="fw-bold fs-5 text-dark">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalCredit)}</span>
                </div>
                <div className="bg-white p-2 rounded-circle shadow-sm"><FaArrowUp className="text-success" /></div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-3 border rounded bg-light d-flex justify-content-between align-items-center h-100">
                <div>
                  <small className="text-muted d-block fw-bold text-uppercase" style={{fontSize: '0.75rem'}}>Genel Bakiye</small>
                  <span className={`fw-bold fs-5 ${finalBalance > 0 ? (account.account_type === 'musteri' ? 'text-danger' : 'text-success') : finalBalance < 0 ? (account.account_type === 'musteri' ? 'text-success' : 'text-danger') : 'text-dark'}`}>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Math.abs(finalBalance))} <small className="text-muted" style={{fontSize: '0.8rem'}}>{finalBalance > 0 ? (account.account_type === 'musteri' ? '(B)' : '(A)') : finalBalance < 0 ? (account.account_type === 'musteri' ? '(A)' : '(B)') : ''}</small></span>
                </div>
                <div className="bg-white p-2 rounded-circle shadow-sm"><FaWallet className="text-primary" /></div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>İşlem Türü</th>
                  <th>Açıklama</th>
                  <th className="text-end">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {/* Devreden Bakiye Satırı (Eğer varsa) */}
                {startDate && carryOverBalance !== 0 && (
                  <tr className="table-secondary fw-bold">
                    <td>{new Date(startDate).toLocaleDateString('tr-TR')}</td>
                    <td colSpan="2">DEVREDEN BAKİYE</td>
                    <td className="text-end">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Math.abs(carryOverBalance))} {carryOverBalance > 0 ? (account.account_type === 'musteri' ? '(B)' : '(A)') : (account.account_type === 'musteri' ? '(A)' : '(B)')}</td>
                  </tr>
                )}

                {filteredHistory.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.date)}</td>
                    <td>
                      {item.source_type === 'islem' ? (
                        item.sale_id ? (
                          <Link to={`/sales/edit/${item.sale_id}`} className="text-decoration-none" title="Satışı Görüntüle">
                            <span className="badge bg-primary">
                              <FaMoneyBillWave className="me-1" />
                              Satış Faturası
                            </span>
                          </Link>
                        ) : (
                          <span className={`badge ${item.type === 'borc' ? 'bg-warning text-dark' : 'bg-info'}`}>
                            <FaMoneyBillWave className="me-1" />
                            {item.type === 'borc' ? 'Borç Fişi' : 'Alacak Fişi'}
                          </span>
                        )
                      ) : item.source_type === 'odeme' ? (
                        <Link to={`/payments/edit/${item.id}`} className="text-decoration-none" title="İşlemi Görüntüle/Düzenle">
                          <span className={`badge ${item.type === 'tahsilat' ? 'bg-success' : 'bg-danger'}`}>
                            <FaWallet className="me-1" />
                            {item.type === 'tahsilat' ? 'Tahsilat' : 'Ödeme'}
                          </span>
                        </Link>
                      ) : item.source_type === 'teslimat' ? (
                        <span 
                          className="badge bg-secondary" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDeliveryClick(item)}
                          title="Teslimat Detaylarını Gör"
                        >
                          <FaTruckLoading className="me-1" /> Teslimat
                        </span>
                      ) : null}
                    </td>
                    <td>{item.description}</td>
                    <td className="text-end fw-bold">
                      {item.type === 'teslimat' ? (
                        <span className="text-muted">-</span>
                      ) : (
                        new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.amount)
                      )}
                    </td>
                  </tr>
                ))}
                {filteredHistory.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center">Hareket bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Teslimat Detay Modalı */}
      {showDeliveryModal && deliveryDetails && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Teslimat Detayı</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeliveryModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info py-2">
                  <strong>Teslimat Tarihi:</strong> {formatDate(deliveryDetails.date)}<br/>
                  <strong>Teslim Edilen Miktar:</strong> {deliveryDetails.quantity} Birim
                </div>
                <h6 className="fw-bold mt-3">Satıştaki Ürünler (Satış #{deliveryDetails.sale_id})</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Ürün</th>
                        <th className="text-center">Sipariş Miktarı</th>
                        <th className="text-center">Birim</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryDetails.saleItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.product_name}</td>
                          <td className="text-center fw-bold">{item.quantity}</td>
                          <td className="text-center">{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <small className="text-muted d-block mt-2">* Bu teslimat, yukarıdaki ürünleri içeren satışa aittir.</small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeliveryModal(false)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentAccountDetailPage;
